import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { SectionTabs } from '@shared/ui';
import { useAbility } from '@features/authorization';
import { cn, getHelpdeskKnowledgePath } from '@shared/lib';

export interface CrmPrimaryNavigationProps {
  className?: string;
  activeSection: 'memberships' | 'profiles' | 'helpdesk' | 'analytics';
  canAccessHelpdesk?: boolean;
  canAccessMemberships: boolean;
  canAccessProfiles: boolean;
  locale: string;
}

/**
 * CRM-level navigation shared by the User Profiles directory and Membership register.
 * It intentionally navigates between pages; it does not create a tabbed Membership detail.
 */
export function CrmPrimaryNavigation({
  className,
  activeSection,
  canAccessHelpdesk = false,
  canAccessMemberships,
  canAccessProfiles,
  locale,
}: CrmPrimaryNavigationProps) {
  const { t } = useTranslation();
  const ability = useAbility();
  const helpdeskPath = canAccessHelpdesk
    ? '/$locale/crm/helpdesk'
    : getHelpdeskKnowledgePath(ability);

  if (!canAccessProfiles && !canAccessMemberships && !helpdeskPath) return null;

  return (
    <nav
      className={cn('mb-4', className)}
      aria-label={t('crm.membershipsRegister.navigation.label', 'CRM sections')}
    >
      <SectionTabs
        activeId={activeSection}
        items={[
          ...(canAccessProfiles
            ? [{ id: 'analytics', label: t('crm.accountCoverage.title', 'Analytics') }]
            : []),
          ...(canAccessProfiles
            ? [
                {
                  id: 'profiles',
                  label: t('crm.membershipsRegister.navigation.profiles', 'Users'),
                },
              ]
            : []),
          ...(helpdeskPath ? [{ id: 'helpdesk', label: t('helpdesk.title', 'Helpdesk') }] : []),
          ...(canAccessMemberships
            ? [
                {
                  id: 'memberships',
                  label: t('crm.membershipsRegister.navigation.memberships', 'Memberships'),
                },
              ]
            : []),
        ]}
        renderLink={(item, { className }) => (
          <Link
            className={className}
            to={
              item.id === 'analytics'
                ? '/$locale/crm/analytics'
                : item.id === 'helpdesk'
                  ? (helpdeskPath ?? '/$locale/crm/helpdesk')
                  : item.id === 'profiles'
                    ? '/$locale/crm'
                    : '/$locale/crm/memberships'
            }
            params={{ locale }}
          >
            {item.label}
          </Link>
        )}
      />
    </nav>
  );
}
